import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import FeedbackModal from "../components/ui/FeedbackModal";

const getPasswordStrength = (password) => {
  const issues = [];

  if (password.length < 8) issues.push("At least 8 characters");
  if (!/[a-z]/.test(password)) issues.push("One lowercase letter");
  if (!/[A-Z]/.test(password)) issues.push("One uppercase letter");
  if (!/[0-9]/.test(password)) issues.push("One number");
  if (!/[^A-Za-z0-9]/.test(password)) issues.push("One special character");

  let label = "Weak";
  if (issues.length <= 1 && password.length >= 8) label = "Strong";
  else if (issues.length <= 3) label = "Medium";

  return {
    label,
    issues,
    score: Math.max(0, 5 - issues.length),
  };
};

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.2 6.2C4.2 7.7 2.8 10 2 12c1.8 4.5 6.2 7 10 7 1.6 0 3.2-.4 4.6-1.1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.9 4.3C10.6 4.1 11.3 4 12 4c4 0 8.2 2.6 10 8-0.5 1.2-1.1 2.4-1.9 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedUser = localStorage.getItem("user");

        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          setUser(parsed);
          setName(parsed.name || "");
        }

        const res = await api.get("/auth/me");
        const freshUser = res.data.user;

        setUser(freshUser);
        setName(freshUser.name || "");
        localStorage.setItem("user", JSON.stringify(freshUser));
      } catch (err) {
        const savedUser = localStorage.getItem("user");
        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          setUser(parsed);
          setName(parsed.name || "");
        }
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const passwordTouched = oldPassword || newPassword || confirmPassword;
  const passwordsMatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const closeFeedback = () => {
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: name.trim(),
      };

      const passwordFlow = oldPassword || newPassword || confirmPassword;

      if (passwordFlow) {
        payload.oldPassword = oldPassword;
        payload.newPassword = newPassword;
        payload.confirmPassword = confirmPassword;
      }

      const res = await api.put("/auth/update-profile", payload);

      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setFeedback({
        open: true,
        type: "success",
        title: "Success",
        message: res.data.message || "Profile updated successfully",
      });
    } catch (err) {
      setFeedback({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Error updating profile",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="rounded-2xl bg-white p-6 shadow-sm">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <FeedbackModal
        open={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={closeFeedback}
      />

      <div className="mx-auto max-w-2xl rounded-3xl bg-white p-8 shadow-sm">
        <div className="flex items-center gap-4 border-b border-slate-100 pb-6">
          <div className="h-16 w-16 overflow-hidden rounded-full border border-slate-200">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                user?.name || "User"
              )}&background=0D8ABC&color=fff&size=128`}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-slate-800">My Profile</h2>
            <p className="text-sm text-slate-500">Update your name and password</p>
          </div>
        </div>

        <form onSubmit={handleUpdate} className="mt-6 space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Full Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none transition focus:border-blue-500"
              placeholder="Enter full name"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ""}
              disabled
              className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-500 outline-none"
            />
            <p className="mt-1 text-xs text-slate-400">
              Email cannot be changed here.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">
              Change Password
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Old Password
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none transition focus:border-blue-500"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword((s) => !s)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-700"
                    aria-label={showOldPassword ? "Hide old password" : "Show old password"}
                  >
                    <EyeIcon open={showOldPassword} />
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none transition focus:border-blue-500"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((s) => !s)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-700"
                    aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  >
                    <EyeIcon open={showNewPassword} />
                  </button>
                </div>

                {newPassword && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-600">
                        Password strength
                      </span>
                      <span
                        className={`font-semibold ${
                          strength.label === "Strong"
                            ? "text-emerald-600"
                            : strength.label === "Medium"
                            ? "text-amber-600"
                            : "text-red-600"
                        }`}
                      >
                        {strength.label}
                      </span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-blue-600 transition-all"
                        style={{ width: `${(strength.score / 5) * 100}%` }}
                      />
                    </div>

                    {strength.issues.length > 0 && (
                      <p className="text-xs text-slate-500">
                        Recommendations: {strength.issues.join(", ")}.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full rounded-xl border px-4 py-3 pr-12 outline-none transition focus:border-blue-500 ${
                      confirmPassword && passwordsMatch
                        ? "border-emerald-300"
                        : confirmPassword && !passwordsMatch
                        ? "border-red-300"
                        : "border-slate-200"
                    }`}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-700"
                    aria-label={
                      showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                    }
                  >
                    <EyeIcon open={showConfirmPassword} />
                  </button>
                </div>

                {confirmPassword && (
                  <p
                    className={`mt-1 text-xs ${
                      passwordsMatch ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                  </p>
                )}
              </div>

              {passwordTouched && (
                <div className="rounded-xl bg-white p-3 text-xs text-slate-600">
                  Use your current password first, then choose a strong new password and confirm it again.
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setOldPassword("");
                setNewPassword("");
                setConfirmPassword("");
              }}
              className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Clear Password
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Saving..." : "Update Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}